import { usdToCents } from './moneyMath.js'

export const MANUAL_APPROVAL_BLOCK_MESSAGE = 'Blockchain verification required before approval can generate commissions.'

const TRUSTED_BLOCKCHAIN_STATUSES = new Set([
 'auto_verified',
 'blockchain_verified',
 'blockchain_verified_approved'
])

export const DEFAULT_UNDERPAY_TOLERANCE_USD = 0.75
export const MANUAL_UNDERPAY_TOLERANCE_USD = 1.00

function toFiniteNumber(value) {
 const parsed = Number(value)
 return Number.isFinite(parsed) ? parsed : null
}

function normalizeReference(value) {
 if (typeof value !== 'string') return ''
 return value.replace(/\s+/g, '').trim()
}

function hasValidReferenceShape(reference) {
 if (!reference) return false
 if (/^0x[a-fA-F0-9]{64}$/.test(reference)) return true
 if (/^[0-9a-fA-F]{64}$/.test(reference)) return true
 if (/^[0-9a-fA-F-]{24,}$/.test(reference)) return true
 return false
}

export function resolveUnderpayToleranceUsd({ requestedToleranceUsd, authorizedManualTolerance } = {}) {
  const requested = toFiniteNumber(requestedToleranceUsd)
  if (!authorizedManualTolerance || requested === null) return DEFAULT_UNDERPAY_TOLERANCE_USD
  if (requested <= DEFAULT_UNDERPAY_TOLERANCE_USD) return DEFAULT_UNDERPAY_TOLERANCE_USD
  if (requested > MANUAL_UNDERPAY_TOLERANCE_USD) return MANUAL_UNDERPAY_TOLERANCE_USD
  return requested
}

export function evaluateManualApprovalBlockchainAttestation(payment, options = {}) {
 const requiredAmount = toFiniteNumber(options.requiredAmount ?? payment?.amount)
 const underpayToleranceUsd = toFiniteNumber(options.underpayToleranceUsd ?? DEFAULT_UNDERPAY_TOLERANCE_USD) ?? DEFAULT_UNDERPAY_TOLERANCE_USD
 const requiredAmountCents = requiredAmount !== null ? usdToCents(requiredAmount) : null
 const toleranceCents = usdToCents(Math.max(0, underpayToleranceUsd))

 const verificationStatus = String(payment?.verification_status || '').trim().toLowerCase()
 const hasTrustedStatus = TRUSTED_BLOCKCHAIN_STATUSES.has(verificationStatus)
 const hasBlockchainVerifiedAt = Boolean(payment?.blockchain_verified_at)

 const blockchainAmount = toFiniteNumber(payment?.blockchain_amount)
 const hasBlockchainAmount = blockchainAmount !== null && blockchainAmount > 0
 const blockchainAmountCents = hasBlockchainAmount ? usdToCents(blockchainAmount) : null

 const txReference = normalizeReference(payment?.transaction_hash)
 const hasTxReference = hasValidReferenceShape(txReference)

 const hasRequiredAmount = requiredAmountCents !== null && requiredAmountCents > 0
 const amountWithinTolerance = Boolean(
 hasBlockchainAmount &&
 hasRequiredAmount &&
 (blockchainAmountCents + toleranceCents >= requiredAmountCents)
 )
 const commissionCoveredShortfall = options.commissionCoveredShortfall === true

 let failureReason = 'OK'
 if (!hasTrustedStatus) {
 failureReason = 'UNTRUSTED_VERIFICATION_STATUS'
 } else if (!hasBlockchainVerifiedAt) {
 failureReason = 'MISSING_BLOCKCHAIN_VERIFIED_AT'
 } else if (!hasBlockchainAmount) {
 failureReason = 'MISSING_BLOCKCHAIN_AMOUNT'
 } else if (!hasRequiredAmount) {
 failureReason = 'INVALID_REQUIRED_AMOUNT'
 } else if (!amountWithinTolerance && !(commissionCoveredShortfall && hasBlockchainAmount)) {
 failureReason = 'BLOCKCHAIN_AMOUNT_BELOW_REQUIRED_TOLERANCE'
 } else if (!hasTxReference) {
 failureReason = 'MISSING_OR_INVALID_TRANSACTION_REFERENCE'
 }

 return {
 passed: failureReason === 'OK',
 failureReason,
 verificationStatus,
 requiredAmount,
 blockchainAmount,
 requiredAmountCents,
 blockchainAmountCents,
 underpayToleranceUsd,
 txReference
 }
}

